// Hideo - Vue dialogs: which ones are open, with what (args), and the stack of those on screen (for Esc and for the
// online sync). Open a dialog with show(name, args) (e.g. show('hook', { id }));
// DialogHost.vue draws it; the dialog closes itself with hide(name). See docs/migration.md, "Dialogs and fields".
import { defineStore } from 'pinia'
import { markRaw, reactive } from 'vue'
import { dialogKey, dialogSaveKey, type DialogEntry } from '../features/dialogs'
import type { SourcePrefill } from '../features/sources'
import type { CategoryGroup, TagGroup } from '../data/types'

/** Text dropped on the Hooks page (src/stores/drop.ts): a new hook starts with it, its title selected */
export interface HookPrefill {
  title?: string
  content?: string
}

/** The dialogs moved to Vue and what each one is opened with */
export interface DialogArgs {
  /** Video dialog (VideoDialog.vue): an existing video, or a new one (id null); the form is in the video store */
  video: { id: string | null }
  /** Hook dialog (HookDialog.vue): an existing hook, or a new one (id null) */
  hook: { id: string | null; prefill?: HookPrefill }
  /** Manage projects (ProjectsDialog.vue) */
  projects: Record<string, never>
  /** Saved plan (PedViewDialog.vue): an existing plan, or a new one ('') */
  pedView: { id: string }
  /** First project of a profile without projects (FirstProjectDialog.vue; ensureFirstProject in the projects store) */
  firstProject: Record<string, never>
  /** Release notes (ChangelogDialog.vue) */
  changelog: Record<string, never>
  /** Source dialog (SourceDialog.vue): an existing source, or a new one (id null); prefill = a link dropped on the Sources page */
  source: { id: string | null; prefill?: SourcePrefill }
  /** Saved video dialog (SavedVideoDialog.vue): an existing one, or a new one (id null); prefill = a link dropped on the
   *  Saved videos page; tag = the page's tag filter (a new video starts with that tag, if its project has it); kind = the
   *  page's category filter (a new video starts with it) */
  saved: { id: string | null; prefill?: { url?: string }; tag?: string; kind?: string }
  /** Manage categories of Sources ('src') or Saved videos ('saved') (CategoryDialog.vue) */
  categories: { group: CategoryGroup }
  /** Link sources and saved videos to the open video (RefsPickDialog.vue, above the video dialog) */
  refsPick: Record<string, never>
  /** A format's card (FormatDialog.vue, Formats page and format labels) */
  format: { id: string }
  /** Manage tags (TagManagerDialog.vue): Hooks ('hook'), Sources ('src') or Saved videos ('saved') tags */
  tags: { kind: TagGroup }
  /** Icon and colour of a tag or a format (LookDialog.vue) */
  look: { target: LookTarget }
  /** Search (SearchDialog.vue, Ctrl/Cmd+K) */
  search: Record<string, never>
  /** Profiles window (ProfilesDialog.vue): profiles, project choice, online saving */
  profiles: Record<string, never>
  /** "Save online" / "Load online": which service (CloudPickDialog.vue) */
  cloudPick: { action: 'save' | 'load'; profileId: string }
  /** "Load online": the profile files found on a service (DriveFilesDialog.vue; the list is in the cloud store) */
  driveFiles: { profileId: string; pid: string }
  /** Profile picker (ProfilePicker.vue): full screen at start-up and from "Switch profile" (picker store) */
  picker: Record<string, never>
  /** Profile card (ProfileEditDialog.vue): an existing profile, or a new one (id null) */
  profileEdit: { id: string | null }
  /** Creator detail (CreatorDialog.vue; creators store openDetail) */
  creator: { id: string }
  /** Creator edit dialog (CreatorEditDialog.vue): an existing creator, or a new one (id null) of a stored type;
   *  fromDetail = opened from the detail, which comes back when it closes (creators store openEdit / closeEdit) */
  creatorEdit: { id: string | null; type: string; fromDetail: boolean }
  /** Analysed video (AnalysedVideoDialog.vue) of a creator: an existing one, or a new one (id null); todoId = a new one
   *  made from that video to analyse (prefilled; saving removes it from the list) */
  analysedVideo: { creatorId: string; id: string | null; todoId?: string | null }
  /** "In your phone calendar": the .ics file of releases and deadlines (IcsDialog.vue) */
  ics: Record<string, never>
  /** "Possible Shorts" of the open video's script (ShortsDialog.vue, above the video dialog) */
  shorts: Record<string, never>
  /** Teleprompter (TeleprompterDialog.vue, above the script editor; the text is in the teleprompter store) */
  teleprompter: Record<string, never>
  /** Script editor (ScriptEditor.vue, always mounted: open while it is on screen; the editor store opens and closes it) */
  editor: Record<string, never>
}
export type DialogName = keyof DialogArgs

/** What the icon/colour dialog (LookDialog.vue) is for: a tag of a project, a format, or the icon (no colour) of an own
 *  category of Sources or Saved videos (CategoryDialog.vue). For a category not added yet (id ''), name and icon are the
 *  ones being typed and `pick` gets the icon chosen (nothing is saved but the image library) */
export type LookTarget = { kind: 'tag'; k: TagGroup; tag: string; pid: string } | { kind: 'format'; id: string }
  | { kind: 'category'; group: CategoryGroup; id: string; name?: string; icon?: string; pick?: (icon: string) => void }

export const useDialogsStore = defineStore('dialogs', () => {
  /** Open dialogs and their arguments */
  const args = reactive<{ [K in DialogName]?: DialogArgs[K] }>({})
  /** Bumped at every opening: opening a dialog that is already open starts it again (DialogHost uses it as key) */
  const seq = reactive<Record<string, number>>({})
  /** Vue dialogs on screen, the topmost last */
  const stack = reactive<DialogEntry[]>([])

  function show<K extends DialogName>(name: K, a: DialogArgs[K]) {
    args[name] = a
    seq[name] = (seq[name] || 0) + 1
  }
  function hide(name: DialogName) { delete args[name] }
  const isOpen = (name: string) => name in args

  function register(e: DialogEntry) { stack.push(markRaw(e)) }
  function unregister(e: DialogEntry) {
    const i = stack.indexOf(e)
    if (i >= 0) stack.splice(i, 1)
  }

  /** Global shortcuts (src/features/keyboard.ts): true when a Vue dialog took the key (Ctrl/Cmd+S or Ctrl/Cmd+Enter saved
   *  the topmost edit dialog, Esc closed it, or it blocks shortcuts) */
  function keydown(e: KeyboardEvent): boolean {
    const top = stack[stack.length - 1]
    if (top && top.save && dialogSaveKey(e)) {
      // A key already taken (Enter picking a row of the @ menu), held down or composing a word does not save
      const taken = e.defaultPrevented || e.isComposing || e.repeat
      e.preventDefault()
      if (!taken) top.save()
      return true
    }
    const r = dialogKey(stack, e.key)
    if (r === 'close') stack[stack.length - 1].close()
    return r !== 'pass'
  }
  /** A dialog where the user types data is open */
  const editing = () => stack.some(x => x.editing)

  return { args, seq, stack, show, hide, isOpen, register, unregister, keydown, editing }
})
